import posthog from 'posthog-js'
import { isTauri } from '@/utils/supabase/tauri-storage'

let initialized = false

// 初期化前に届いた identify を保留する。
// 静的エクスポートでは PostHogProvider (useSearchParams のため Suspense 内) の init が
// AuthProvider の identify effect より後に走るため、キャッシュ済みセッションで即ログイン状態に
// なる人は identify が「未初期化」で捨てられ、匿名 person のまま使い続けていた (2026-10-11 分析)。
let pendingIdentify: { userId: string; properties?: Record<string, unknown> } | null = null

// 開発者の手元の本番ビルド (npm run build && npm start や Playwright での確認) は localhost で動く。
// これが本番の PostHog プロジェクトにイベントを送ると実利用の数字に混ざる
// (2026-10-23 の分析: localhost から 62 events)。ただし Tauri (デスクトップ) は macOS で
// `tauri://localhost` = hostname が localhost になるので、Tauri は除く。
function isLocalDevelopmentHost(): boolean {
  if (typeof window === 'undefined') return false
  if (isTauri()) return false
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost')
}

function isEnabled(): boolean {
  if (process.env.NODE_ENV !== 'production') return false
  if (typeof window === 'undefined') return false
  if (isLocalDevelopmentHost()) return false
  return Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY)
}

export function initPostHog(): void {
  if (initialized) return
  if (process.env.NODE_ENV !== 'production') return
  if (typeof window === 'undefined') return
  if (isLocalDevelopmentHost()) return

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

  if (!key) {
    console.warn('[PostHog] NEXT_PUBLIC_POSTHOG_KEY is not set. PostHog disabled.')
    return
  }

  try {
    posthog.init(key, {
      api_host: host,
      persistence: 'localStorage',
      capture_pageview: false,
      autocapture: true,
      // 未捕捉の例外・Promise rejection・console.error を PostHog Error Tracking に送る。
      // React error #185 のような本番でのみ出るエラーの原因コンポーネントを特定するため。
      capture_exceptions: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskTextFn: () => '***',
        blockSelector: '[data-ph-no-capture]',
      },
      loaded: (instance) => {
        instance.register({
          app: isTauri() ? 'desktop' : 'web',
          app_version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
        })
      },
    })
    initialized = true
    if (pendingIdentify) {
      const { userId, properties } = pendingIdentify
      pendingIdentify = null
      identifyUser(userId, properties)
    }
  } catch (e) {
    console.warn('[PostHog] init failed', e)
  }
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!isEnabled() || !initialized) return
  try {
    posthog.capture(event, properties)
  } catch (e) {
    console.warn('[PostHog] capture failed', e)
  }
}

export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!isEnabled()) return
  if (!initialized) {
    pendingIdentify = { userId, properties }
    return
  }
  try {
    posthog.identify(userId, properties)
    posthog.register({ is_authenticated: true })
  } catch (e) {
    console.warn('[PostHog] identify failed', e)
  }
}

export function resetUser(): void {
  pendingIdentify = null
  if (!isEnabled() || !initialized) return
  try {
    posthog.reset()
    posthog.register({ is_authenticated: false })
  } catch (e) {
    console.warn('[PostHog] reset failed', e)
  }
}

export function capturePageview(url: string): void {
  if (!isEnabled() || !initialized) return
  try {
    posthog.capture('$pageview', { $current_url: url })
  } catch (e) {
    console.warn('[PostHog] pageview failed', e)
  }
}

// テスト用のみ。プロダクションコードからは呼ばないこと
export function __resetForTesting(): void {
  initialized = false
  pendingIdentify = null
}
